import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useNavigate} from 'react-router-dom';
import AdminOrdersService, {type OrderListFilters, type OrderType} from '../services/adminOrdersService';
import SectionHeader from '@/shared/components/SectionHeader';
import Badge from '@/shared/components/Badge';
import {dollars} from '@/shared/utils/money';

const TYPE_LABELS: Record<OrderType, string> = {
    custom_kit: 'Custom Kit',
    curated_enrollment: 'Curated Enrollment',
    recurring: 'Recurring',
    shop: 'Shop',
    woocommerce: 'WooCommerce',
    other: 'Other',
};

const PUSH_STATUS_LABELS: Record<string, string> = {
    pending: 'Pending',
    processing: 'Processing',
    pushed: 'Pushed',
    failed: 'Failed',
};

// `onSelectOrder` — when embedded in the admin shell, the parent handles
// list→detail in-place. Standalone (deep-link) usage falls back to routing.
const AdminOrdersView: React.FC<{onSelectOrder?: (id: string) => void}> = ({onSelectOrder}) => {
    const navigate = useNavigate();
    const openOrder = (id: string) => (onSelectOrder ? onSelectOrder(id) : navigate(`/admin/orders/${id}`));
    const [filters, setFilters] = useState<OrderListFilters>({page: 1, page_size: 25});

    const {data, isLoading, isError} = useQuery({
        queryKey: ['admin', 'orders', filters],
        queryFn: () => AdminOrdersService.list(filters),
        staleTime: 30_000,
    });

    const set = (patch: Partial<OrderListFilters>) => setFilters((f) => ({...f, ...patch, page: 1}));
    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

    return (
        <div className="p-6 space-y-4">
            <SectionHeader title="Orders" />

            <div className="flex flex-wrap gap-2 items-end">
                <input
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                    placeholder="Search customer / email / order #"
                    defaultValue={filters.q ?? ''}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') set({q: (e.target as HTMLInputElement).value.trim() || undefined});
                    }}
                />
                <select
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={filters.type ?? ''}
                    onChange={(e) => set({type: (e.target.value || undefined) as OrderType | undefined})}>
                    <option value="">All types</option>
                    {(Object.keys(TYPE_LABELS) as OrderType[]).map((t) => (
                        <option key={t} value={t}>
                            {TYPE_LABELS[t]}
                        </option>
                    ))}
                </select>
                <input
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Fulfillment status"
                    defaultValue={filters.fulfillment_status ?? ''}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter')
                            set({fulfillment_status: (e.target as HTMLInputElement).value.trim() || undefined});
                    }}
                />
                <select
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={filters.push_status ?? ''}
                    onChange={(e) => set({push_status: e.target.value || undefined})}>
                    <option value="">All push statuses</option>
                    {Object.entries(PUSH_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
                <input
                    type="date"
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={filters.date_from ?? ''}
                    onChange={(e) => set({date_from: e.target.value || undefined})}
                />
                <input
                    type="date"
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                    value={filters.date_to ?? ''}
                    onChange={(e) => set({date_to: e.target.value || undefined})}
                />
            </div>

            {isLoading && <p className="text-gray-500">Loading…</p>}
            {isError && <p className="text-mld-status-high">Could not load orders.</p>}

            {data && (
                <>
                    <div className="overflow-x-auto bg-white rounded-2xl border border-mld-panel-border shadow-sm">
                        <table className="min-w-full text-sm">
                            <thead className="text-left text-gray-500 border-b border-mld-panel-border">
                                <tr>
                                    <th className="px-4 py-3">Order #</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Type</th>
                                    <th className="px-4 py-3">Total</th>
                                    <th className="px-4 py-3">Payment</th>
                                    <th className="px-4 py-3">Fulfillment</th>
                                    <th className="px-4 py-3">Sub?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.orders.map((o) => (
                                    <tr
                                        key={o.id}
                                        className="border-b border-mld-panel-border hover:bg-mld-primary/5 cursor-pointer"
                                        onClick={() => openOrder(o.id)}>
                                        <td className="px-4 py-3 font-medium text-mld-text-primary">
                                            {o.order_number}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{o.created_at.slice(0, 10)}</td>
                                        <td className="px-4 py-3">
                                            <div className="text-mld-text-primary">{o.customer_name}</div>
                                            <div className="text-gray-500 text-xs">{o.customer_email}</div>
                                        </td>
                                        <td className="px-4 py-3">{TYPE_LABELS[o.type]}</td>
                                        <td className="px-4 py-3">{dollars(o.total_cents)}</td>
                                        <td className="px-4 py-3">{o.payment_status ?? '—'}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span>{o.fulfillment_status}</span>
                                                {o.push_status === 'failed' && <Badge variant="error">failed</Badge>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">{o.is_subscription ? 'Yes' : ''}</td>
                                    </tr>
                                ))}
                                {data.orders.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                                            No orders match.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{data.total} orders</span>
                        <div className="flex items-center gap-2">
                            <button
                                className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                                disabled={(filters.page ?? 1) <= 1}
                                onClick={() => setFilters((f) => ({...f, page: (f.page ?? 1) - 1}))}>
                                Prev
                            </button>
                            <span>
                                Page {filters.page ?? 1} / {totalPages}
                            </span>
                            <button
                                className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-40"
                                disabled={(filters.page ?? 1) >= totalPages}
                                onClick={() => setFilters((f) => ({...f, page: (f.page ?? 1) + 1}))}>
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminOrdersView;
